import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast } from 'react-hot-toast';
import Pagination from '../components/Pagination';

const ReferenceMaster = ({ type, title }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [editingId, setEditingId] = useState(null);
  
  // Form State
  const [newValue, setNewValue] = useState('');
  const [status, setStatus] = useState('active');

  const [itemToDelete, setItemToDelete] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/reference-values?type=${type}`, { withCredentials: true });
      setData(res.data);
    } catch (error) {
      console.error(error);
      toast.error(`Failed to load ${title}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [type]);

  const openAddModal = () => {
    setModalMode('add');
    setEditingId(null);
    setNewValue('');
    setStatus('active');
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setModalMode('edit');
    setEditingId(item.id);
    setNewValue(item.value);
    setStatus(item.status || 'active');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!newValue.trim()) return;
    
    try {
      if (modalMode === 'add') {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/reference-values`, { 
          type, 
          value: newValue.trim(),
          status 
        }, { withCredentials: true });
        toast.success(`${title} added successfully!`);
      } else {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/reference-values/${editingId}`, { 
          value: newValue.trim(),
          status 
        }, { withCredentials: true });
        toast.success(`${title} updated successfully!`);
      }
      closeModal();
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error(`Failed to save ${title}`);
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/reference-values/${itemToDelete}`, { withCredentials: true });
      toast.success('Deleted successfully!');
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete item');
    } finally {
      setItemToDelete(null);
    }
  };

  return (
    <Layout title={title} subtitle={`Manage all ${title.toLowerCase()}`}>
      <div className="bg-slate-900/20 border border-slate-900 rounded-3xl p-6 mt-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-bold text-white text-lg">{title} Directory</h3>
          <div className="flex gap-3">
            <input 
              type="text" 
              placeholder={`Search ${title.toLowerCase()}...`}
              className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm outline-none focus:border-emerald-500 w-64 transition"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button 
              onClick={openAddModal}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold px-4 py-2 rounded-xl text-sm transition"
            >
              Add Data
            </button>
          </div>
        </div>

        <div className="w-full">
          {loading ? (
            <div className="text-center text-slate-400 py-10">Loading...</div>
          ) : data.length === 0 ? (
            <div className="text-center text-slate-400 py-10">No {title.toLowerCase()} found.</div>
          ) : (
            <div className="rounded-2xl border border-slate-800/50 overflow-hidden bg-[#0f172a]">
              <table className="w-full text-left">
                <thead className="bg-slate-900/50 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  <tr>
                    <th className="p-4 w-[10%]">S.No</th>
                    <th className="p-4 w-[50%]">Name</th>
                    <th className="p-4 w-[20%]">Status</th>
                    <th className="p-4 w-[20%] text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                {(() => {
                  const filteredData = data.filter(item => item.value.toLowerCase().includes(searchTerm.toLowerCase()));
                  const totalPages = Math.ceil(filteredData.length / recordsPerPage);
                  const paginatedData = filteredData.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);
                  
                  return (
                    <>
                      {paginatedData.map((item, index) => (
                        <tr key={item.id} className="border-b border-slate-800/50 hover:bg-slate-800/20 transition group">
                          <td className="p-4 text-sm text-slate-300 font-medium">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                    <td className="p-4 text-sm text-white font-semibold">{item.value}</td>
                    <td className="p-4 text-sm">
                      <span className={`px-2 py-1 rounded-md text-xs font-semibold ${item.status === 'active' || !item.status ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'}`}>
                        {item.status === 'inactive' ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-center items-center gap-2">
                        <button 
                          onClick={() => openEditModal(item)}
                          className="w-8 h-8 rounded-full bg-blue-500/10 text-blue-500 flex items-center justify-center hover:bg-blue-500/20 transition-colors"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                          </svg>
                        </button>
                        <button 
                          onClick={() => setItemToDelete(item.id)}
                          className="w-8 h-8 rounded-full bg-rose-500/10 text-rose-500 flex items-center justify-center hover:bg-rose-500/20 transition-colors"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                </>
              );
            })()}
            </tbody>
          </table>
          
          {(() => {
             const filteredData = data.filter(item => item.value.toLowerCase().includes(searchTerm.toLowerCase()));
             const totalPages = Math.ceil(filteredData.length / recordsPerPage);
             return totalPages > 1 ? (
               <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} totalItems={filteredData.length} />
             ) : null;
          })()}
          </div>
        )}
      </div>
      </div>
      
      {/* ── Add/Edit Modal ─────────────────────────────────────── */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">
                {modalMode === 'add' ? `Add ${title}` : `Edit ${title}`}
              </h3>
              <button onClick={closeModal} className="text-slate-400 hover:text-white transition">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            
            <form onSubmit={handleSave}>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Name</label>
                  <input 
                    type="text" 
                    placeholder="Enter name..."
                    className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl p-3 focus:outline-none focus:border-emerald-500 transition"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Status</label>
                  <select
                    className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl p-3 focus:outline-none focus:border-emerald-500 transition appearance-none"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <button 
                  type="button"
                  onClick={closeModal}
                  className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-semibold py-3 rounded-xl transition"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={!newValue.trim()}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold py-3 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {modalMode === 'add' ? 'Save' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ─────────────────────────────────────── */}
      {itemToDelete !== null && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#0f172a] border border-rose-500/30 rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-rose-500/10 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Delete Item</h3>
              <p className="text-slate-400 text-sm mb-6">
                Are you sure you want to delete this item? This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button 
                type="button"
                onClick={() => setItemToDelete(null)}
                className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-semibold py-2.5 rounded-xl transition"
              >
                Cancel
              </button>
              <button 
                type="button"
                onClick={confirmDelete}
                className="flex-1 bg-rose-600 hover:bg-rose-500 text-white font-semibold py-2.5 rounded-xl transition shadow-[0_0_15px_rgba(225,29,72,0.4)]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ReferenceMaster;
